import React from "react";
import Widget from "../Widget";
import { useTasksWidget } from "./TaskWidget.hooks";
import { Alert, Box, Skeleton, styled } from "@mui/material";
import { DotLegalHeader, DotLegalSwitch } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation";
import NavigateTo from "../navigateTo/NavigateTo";
import WhiteDashboardBox from "../WhiteDashboardBox.tsx";
import TasksWidgetStatus from "./tasksWidgetStatus/TasksWidgetStatus.tsx";
import TaskWidgetEmptyState from "./taskWidgetEmptyState/TaskWidgetEmptyState.tsx";
import VirtualizedTaskList from "./VirtualizedTaskList.tsx";
import { useTaskUrlBinding } from "../../taskManagement/useTaskUrlBinding";

export interface TasksWidgetProps {
    onlyUserSpecific: boolean;
    selectedGroupCompany: string | undefined;
    showCurrentMonth?: boolean;
}

export const SwitchNumbersCount = styled("span")({
    marginLeft: 8,
    borderRadius: "50%",
    padding: "2px 6px",
    backgroundColor: "white",
    fontWeight: 500,
});

function TasksWidget(props: TasksWidgetProps) {
    // Deliberately here rather than in useTasksWidget: the compliance report shares that hook and renders
    // two widgets side by side, which would put two owners of the taskId parameter on one page.
    useTaskUrlBinding();

    const { translateString } = useTranslation();
    const {
        isLoading,
        tasks,
        noOverdueTaskStreakInMonths,
        currentMonthTasksLimitHit,
        overdueTasksLimitHit,
        progress,
        isEnabled,
        showCurrentMonth,
        numberOfOverdueTasks,
        numberOfTasksThisMonth,
        setShowCurrentMonth,
        getTaskManagementUrl,
    } = useTasksWidget(props);

    const dotLegalSwitch = (
        <DotLegalSwitch
            onChange={() => setShowCurrentMonth(!showCurrentMonth)}
            checked={showCurrentMonth}
            leftLabel={
                <Box component={"span"}>
                    {translateString("overdueTask")}
                    <SwitchNumbersCount>{numberOfOverdueTasks}</SwitchNumbersCount>
                </Box>
            }
            rightLabel={
                <Box component={"span"}>
                    {translateString("thisMonth")}
                    <SwitchNumbersCount>{numberOfTasksThisMonth}</SwitchNumbersCount>
                </Box>
            }
        />
    );

    return (
        <Widget disabled={!isEnabled}>
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <DotLegalHeader headerStyle={"medium"} marginBottom={0}>
                    {translateString("tasks").toUpperCase()}
                </DotLegalHeader>

                {isLoading ? <Skeleton>{dotLegalSwitch}</Skeleton> : dotLegalSwitch}
                {((showCurrentMonth && currentMonthTasksLimitHit) || (!showCurrentMonth && overdueTasksLimitHit)) && (
                    <Alert sx={{ marginTop: 0.5 }} severity="warning">
                        {showCurrentMonth ? translateString("taskWidgetCurrentMonthLimitWarning") : translateString("taskWidgetOverdueLimitWarning")}
                    </Alert>
                )}
                <TasksWidgetStatus numberOfTasks={tasks.length} showCurrentMonth={showCurrentMonth} progress={progress} />
                {isLoading ? (
                    <WhiteDashboardBox marginTop={2} fullHeight isLoading={isLoading}>
                        -
                    </WhiteDashboardBox>
                ) : tasks.length > 0 ? (
                    <Box
                        mt={2}
                        sx={{
                            marginRight: `-15px`,
                            height: "100%",
                            "& .MuiPaper-root": {
                                boxShadow: "none",
                            },
                        }}
                    >
                        <VirtualizedTaskList tasks={tasks} />
                    </Box>
                ) : (
                    <TaskWidgetEmptyState showCurrentMonth={showCurrentMonth} noOverdueTaskStreakInMonths={noOverdueTaskStreakInMonths} />
                )}
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <NavigateTo
                        color="purple"
                        text={showCurrentMonth ? translateString("goToCurrentTasks") : translateString("goToOverdueTasks")}
                        to={getTaskManagementUrl(showCurrentMonth ? `?months=${new Date().getMonth() + 1}` : "?months=&overdueTasks=true")}
                    />
                </Box>
            </Box>
        </Widget>
    );
}

export default TasksWidget;
